/*
 * Magnimoo geo detection (Europe market focus, 2026-10-19).
 *
 *   window.MagnimooGeo.country(): Promise<string|null>   ISO country code, or null when unknown
 *   window.MagnimooGeo.isUS():    Promise<boolean>
 *   window.MagnimooGeo.peek():    string|null|undefined  sync read: undefined while still detecting
 *
 * Source: GET /browsing_context_suggestions.json?country[enabled]=true -> detected_values.country.handle
 * (Shopify's own geolocation, same origin, no third party). A successful result is cached in
 * sessionStorage['mm_geo_country'].
 *
 * Test overrides (never cached): ?mm_geo=XX forces country XX, ?mm_geo=fail forces null.
 *
 * FAIL OPEN, always: any error, timeout, blocked request or unreadable storage resolves null, and
 * isUS() is then false. Nothing here may ever stop a shopper from buying. Everything is client-side,
 * so the server HTML (and therefore Googlebot) never depends on it.
 */
(function () {
  'use strict';
  if (window.MagnimooGeo) return;

  var CACHE_KEY = 'mm_geo_country';
  var ENDPOINT = '/browsing_context_suggestions.json?country[enabled]=true';
  var TIMEOUT_MS = 2500;
  var CODE = /^[A-Za-z]{2}$/;

  var settled = false;
  var value = null;
  var pending = null;

  function readCache() {
    try {
      var cached = window.sessionStorage.getItem(CACHE_KEY);
      return cached && CODE.test(cached) ? cached.toUpperCase() : null;
    } catch (e) {
      return null;
    }
  }

  function writeCache(code) {
    try {
      window.sessionStorage.setItem(CACHE_KEY, code);
    } catch (e) { /* storage unavailable: just detect again on the next page */ }
  }

  // undefined = no override, null = forced failure, 'XX' = forced country
  function readOverride() {
    try {
      var raw = new URLSearchParams(window.location.search).get('mm_geo');
      if (!raw) return undefined;
      if (raw.toLowerCase() === 'fail') return null;
      if (CODE.test(raw)) return raw.toUpperCase();
    } catch (e) { /* ignore */ }
    return undefined;
  }

  function settle(code) {
    if (!settled) {
      settled = true;
      value = code || null;
    }
    return value;
  }

  function request() {
    return new Promise(function (resolve) {
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = setTimeout(function () {
        if (controller) { try { controller.abort(); } catch (e) { /* ignore */ } }
        resolve(null);
      }, TIMEOUT_MS);
      function done(code) { clearTimeout(timer); resolve(code); }

      try {
        window.fetch(ENDPOINT, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: controller ? controller.signal : undefined
        })
          .then(function (response) { return response.ok ? response.json() : null; })
          .then(function (data) {
            var handle = data && data.detected_values && data.detected_values.country &&
              data.detected_values.country.handle;
            done(typeof handle === 'string' && CODE.test(handle) ? handle.toUpperCase() : null);
          })
          .catch(function () { done(null); });
      } catch (e) {
        done(null);
      }
    });
  }

  function country() {
    if (pending) return pending;
    var override = readOverride();
    if (override !== undefined) {
      pending = Promise.resolve(settle(override));
      return pending;
    }
    var cached = readCache();
    if (cached) {
      pending = Promise.resolve(settle(cached));
      return pending;
    }
    pending = request().then(function (code) {
      if (code) writeCache(code);
      return settle(code);
    }, function () { return settle(null); });
    return pending;
  }

  function isUS() {
    return country().then(function (code) { return code === 'US'; }, function () { return false; });
  }

  function peek() {
    return settled ? value : undefined;
  }

  window.MagnimooGeo = { country: country, isUS: isUS, peek: peek };

  // Start detecting right away so the answer is ready (and usually synchronous via peek) by the
  // time a shopper reaches Add to cart. Overrides and the cache settle immediately.
  try {
    var early = readOverride();
    if (early !== undefined) settle(early);
    else {
      var warm = readCache();
      if (warm) settle(warm);
    }
    country();
  } catch (e) { /* fail open */ }
})();
